// Summary over the current filter or selection: volume, PIN vs plan, revenue, blended Rev % / price.
// Blend is computed client-side with the same rule as pipeline_domain::blend (see lib/blend.ts).
import { Info } from "lucide-react";
import type { Loan } from "../lib/types";
import { blend } from "../lib/blend";
import { money, moneyCompact, pct1 } from "../lib/format";
import { monthLabel } from "../lib/dates";
import { cx, Progress, Tip } from "./ui";

export interface PinInfo {
  month: string;
  volume: number;
  notApproved: number;
  plan: number | null;
}

/** `pin` null hides PIN against plan; `label` names the loans in view (default "In view"). */
export function SummaryBar(props: { loans: Loan[]; selection: boolean; pin: PinInfo | null; target: number; label?: string; onClearSelection?: () => void }) {
  const { loans, selection, pin, target } = props;
  const b = blend(loans.map((l) => ({ amount: l.amount, price: l.price })));
  const pricedShare = b.volume > 0 ? (b.pricedVolume / b.volume) * 100 : null;
  const simple = (() => {
    const ps = loans.map((l) => Number(l.price)).filter((p) => l_ok(p));
    return ps.length ? ps.reduce((a, p) => a + p, 0) / ps.length : null;
  })();
  const gapPts = b.blendedRevPct !== null ? b.blendedRevPct - (target - 100) : null;
  const pinPct = pin && pin.plan ? (pin.volume / pin.plan) * 100 : null;
  const comp = loans.reduce((a, l) => a + (l.comp?.commission ? Number(l.comp.commission) : 0), 0);
  const hasComp = loans.some((l) => l.comp?.commission);

  return (
    <div
      className={cx(
        "grid grid-cols-2 items-stretch gap-x-4 gap-y-2 rounded-[10px] border px-3 py-2.5 sm:flex sm:flex-wrap sm:gap-x-6 sm:px-4",
        selection ? "border-focus/50 bg-selected" : "border-subtle bg-surface shadow-[var(--shadow-1)]",
      )}
      aria-live="polite"
    >
      <Metric label={selection ? "Selection" : (props.label ?? "In view")} value={money(b.volume)} sub={`${b.loans} loan${b.loans === 1 ? "" : "s"}`} />
      {pin && !selection ? (
        <div className="col-span-2 min-w-[220px] flex-1 sm:max-w-[320px]">
          <div className="eyebrow text-ink-3">PIN · {monthLabel(pin.month, true)}</div>
          <div className="flex items-baseline gap-1.5 tnum">
            <span className="text-[18px] font-semibold text-ink">{moneyCompact(pin.volume) || "$0"}</span>
            <span className="text-ink-2">{pin.plan ? `of ${moneyCompact(pin.plan)} plan` : "· no plan set"}</span>
            {pinPct !== null ? (
              <span className={cx("ml-auto text-[13px] font-semibold", pinPct >= 100 ? "text-accent-text" : "text-ink-2")}>{pct1(pinPct)}</span>
            ) : null}
          </div>
          {pin.plan ? <Progress value={pinPct} label="PIN volume against plan" /> : null}
          {pin.notApproved > 0 ? (
            <div className="mt-0.5 text-[12px] text-warning tnum">+ {moneyCompact(pin.notApproved)} PIN not yet at CLA</div>
          ) : null}
        </div>
      ) : null}
      <Metric
        label="Revenue"
        value={money(b.revenue)}
        sub={b.pricedLoans < b.loans ? `${b.pricedLoans} of ${b.loans} priced` : "all priced"}
        tip={
          <>
            <b>Revenue $</b> = Σ amount × (price − 100) / 100 over priced loans.
            <br />
            Unpriced loans add $0, never a negative.
          </>
        }
      />
      <div className="sm:min-w-[180px]">
        <div className="eyebrow flex items-center gap-1 text-ink-3">
          Blended Rev %
          <Tip
            content={
              <div className="space-y-1.5">
                <div className="font-semibold">Blended rate = volume-weighted average</div>
                <div className="tnum">
                  Σ(amount × (price − 100)/100) ÷ Σ priced amount
                  <br />= {money(b.revenue)} ÷ {money(b.pricedVolume)}
                  <br />= <b>{b.blendedRevPct !== null ? `${b.blendedRevPct.toFixed(3)}%` : "—"}</b>
                  {b.blendedPrice !== null ? (
                    <>
                      {" "}
                      → blended price <b>{b.blendedPrice.toFixed(3)}</b>
                    </>
                  ) : null}
                </div>
                <div className="text-ink-2 tnum">
                  Priced {b.pricedLoans} of {b.loans} loans{pricedShare !== null ? ` · ${Math.round(pricedShare)}% of volume` : ""}. Unpriced loans are
                  left out of both sides.
                </div>
                {simple !== null && b.blendedPrice !== null && Math.abs(simple - b.blendedPrice) >= 0.001 ? (
                  <div className="text-ink-2 tnum">
                    A simple average would say {simple.toFixed(3)}; big loans count more, so we weight by amount.
                  </div>
                ) : null}
                {gapPts !== null ? (
                  <div className={cx("tnum", gapPts >= 0 ? "text-success" : "text-warning")}>
                    Target {target.toFixed(3)} · {gapPts >= 0 ? "+" : "−"}
                    {Math.abs(gapPts).toFixed(3)} pts = {gapPts >= 0 ? "+" : "−"}
                    {money(Math.abs((gapPts * b.pricedVolume) / 100))} on this volume
                  </div>
                ) : null}
              </div>
            }
          >
            <button className="focus-ring rounded-full text-ink-3 hover:text-ink" aria-label="How blended Rev % is calculated">
              <Info size={13} />
            </button>
          </Tip>
        </div>
        <div className="flex items-baseline gap-2 tnum">
          <span className={cx("text-[18px] font-semibold", b.blendedPrice !== null && b.blendedPrice >= target ? "text-accent-text" : "text-ink")}>
            {b.blendedRevPct !== null ? `${b.blendedRevPct.toFixed(3)}%` : "—"}
          </span>
          <span className="text-ink-2">{b.blendedPrice !== null ? `price ${b.blendedPrice.toFixed(3)}` : "no prices yet"}</span>
        </div>
        <div className="text-[12px] text-ink-3 tnum">
          priced {b.pricedLoans} of {b.loans}
        </div>
      </div>
      {hasComp ? <Metric label="Comp" value={money(comp)} sub="your loans" /> : null}
      {selection && props.onClearSelection ? (
        <button className="focus-ring ml-auto self-center rounded-md px-2 py-1 text-[13px] text-link hover:underline" onClick={props.onClearSelection}>
          Clear selection
        </button>
      ) : null}
    </div>
  );
}

function l_ok(p: number) {
  return Number.isFinite(p) && p > 0;
}

function Metric({ label, value, sub, tip }: { label: string; value: string; sub?: string; tip?: React.ReactNode }) {
  return (
    <div className="sm:min-w-[120px]">
      <div className="eyebrow flex items-center gap-1 text-ink-3">
        {label}
        {tip ? (
          <Tip content={tip}>
            <button className="focus-ring rounded-full text-ink-3 hover:text-ink" aria-label={`About ${label}`}>
              <Info size={13} />
            </button>
          </Tip>
        ) : null}
      </div>
      <div className="text-[18px] font-semibold text-ink tnum">{value || "$0"}</div>
      {sub ? <div className="text-[12px] text-ink-3">{sub}</div> : null}
    </div>
  );
}
